<template>
  <div
    class="form-group"
    :class="{
      'form-group--has-error': localError,
      'form-group--has-success': !localError && touched,
    }"
  >
    <label v-if="label" :for="id">{{ label }}</label>
    <div class="notes-editor" :class="{ 'notes-editor--has-error': localError }">
      <div class="editor-toolbar">
        <div class="toolbar-section">
          <button
            v-for="field in availableFields"
            :key="field.id"
            @click.prevent="insertField(field)"
            class="toolbar-button field-button"
            :class="`field-button--${field.type}`"
            :title="`${field.label} (${field.type})`"
          >
            <BaseIcon :name="getFieldIcon(field.type)" />
            {{ field.label }}
          </button>
        </div>
      </div>
      <div ref="editorRef" class="editor-content" />
    </div>
    <span v-if="localError" class="form-group__error">{{ localError }}</span>
  </div>
</template>

<script>
  import { defineComponent, ref, onMounted, onBeforeUnmount, watch, computed } from 'vue';
  import { Editor } from '@tiptap/vue-3';
  import StarterKit from '@tiptap/starter-kit';
  import { Extension, Node } from '@tiptap/core';
  import { useFormStore } from '@/store/formStore';
  import BaseIcon from '@/components/partials/BaseIcon.vue';

  const ALLOWED_TYPES = ['formula', 'slider', 'text', 'number', 'long-answer', 'date'];

  const FieldNode = Node.create({
    name: 'fieldNode',
    group: 'inline',
    inline: true,
    atom: true,

    addAttributes() {
      return {
        id: {
          default: null,
        },
        label: {
          default: null,
        },
        type: {
          default: null,
        },
      };
    },

    parseHTML() {
      return [
        {
          tag: 'span[data-field-id]',
        },
      ];
    },

    renderHTML({ node }) {
      return [
        'span',
        {
          class: 'field-tag-wrapper',
          'data-field-label': node.attrs.label,
        },
        [
          'span',
          {
            class: `field-tag field-tag--${node.attrs.type}`,
            'data-field-id': node.attrs.id,
          },
          node.attrs.label,
        ],
      ];
    },

    toText({ node }) {
      return `{${node.attrs.id}}`;
    },
  });

  export default defineComponent({
    name: 'NotesEditor',
    components: { BaseIcon },

    props: {
      modelValue: {
        type: String,
        default: '',
      },
      label: {
        type: String,
        default: '',
      },
      error: {
        type: String,
        default: '',
      },
      id: {
        type: String,
        default: () => `notes-${Math.random().toString(36).substr(2, 9)}`,
      },
      availableFields: {
        type: Array,
        default: () => [],
      },
    },

    emits: ['update:modelValue', 'error'],

    setup(props, { emit }) {
      const formStore = useFormStore();
      const editorRef = ref(null);
      const localError = ref(props.error);
      const touched = ref(false);
      let editor = null;

      const getFieldIcon = (type) => {
        switch (type) {
          case 'formula':
            return 'formula';
          case 'slider':
          case 'number':
            return 'hash';
          case 'text':
          case 'long-answer':
            return 'type';
          default:
            return 'hash';
        }
      };
      const insertField = (field) => {
        editor
          .chain()
          .focus()
          .insertContent({
            type: 'fieldNode',
            attrs: {
              id: field.id,
              label: field.label,
              type: field.type,
            },
          })
          .insertContent(' ')
          .run();
        touched.value = true;
      };

      const getContent = () => {
        let content = '';
        editor.state.doc.descendants((node) => {
          if (node.type.name === 'fieldNode') {
            content += `{${node.attrs.id}}`;
          } else if (node.type.name === 'paragraph' && content.length > 0) {
            content += '\n';
          } else if (node.text) {
            content += node.text;
          }
        });
        return content.trim();
      };

      onMounted(() => {
        editor = new Editor({
          element: editorRef.value,
          extensions: [StarterKit, FieldNode],
          content: '',
          onUpdate: ({ editor }) => {
            const content = getContent();
            emit('update:modelValue', content);
          },
        });

        if (props.modelValue) {
          const lines = props.modelValue.split('\n');
          lines.forEach((line, index) => {
            if (index > 0) {
              editor.chain().insertContent('\n').run();
            }

            const parts = line.match(/(\€?\{[^}]+\}|\€|\s+|\S+)/g) || [];
            for (const part of parts) {
              const fieldMatch = part.match(/^\€?\{([\w-]+)\}$/);
              if (fieldMatch) {
                const fieldId = fieldMatch[1];
                const field = props.availableFields.find((f) => f.id === fieldId);
                if (field) {
                  if (part.startsWith('€')) {
                    editor.chain().insertContent('€').run();
                  }
                  editor
                    .chain()
                    .insertContent({
                      type: 'fieldNode',
                      attrs: {
                        id: field.id,
                        label: field.label,
                        type: field.type,
                      },
                    })
                    .run();
                }
              } else {
                editor.chain().insertContent(part).run();
              }
            }
          });
        }
      });

      watch(
        () => props.modelValue,
        (newValue) => {
          if (editor && newValue !== getContent()) {
            editor.commands.setContent('');
            if (newValue) {
              const lines = newValue.split('\n');
              lines.forEach((line, index) => {
                if (index > 0) {
                  editor.chain().insertContent('\n').run();
                }

                const parts = line.match(/(\€?\{[^}]+\}|\€|\s+|\S+)/g) || [];
                for (const part of parts) {
                  const fieldMatch = part.match(/^\€?\{([\w-]+)\}$/);
                  if (fieldMatch) {
                    const fieldId = fieldMatch[1];
                    const field = props.availableFields.find((f) => f.id === fieldId);
                    if (field) {
                      if (part.startsWith('€')) {
                        editor.chain().insertContent('€').run();
                      }
                      editor
                        .chain()
                        .insertContent({
                          type: 'fieldNode',
                          attrs: {
                            id: field.id,
                            label: field.label,
                            type: field.type,
                          },
                        })
                        .run();
                    }
                  } else {
                    editor.chain().insertContent(part).run();
                  }
                }
              });
            }
          }
        }
      );

      onBeforeUnmount(() => {
        if (editor) {
          editor.destroy();
        }
      });

      return {
        editorRef,
        insertField,
        localError,
        touched,
        getFieldIcon,
      };
    },
  });
</script>

<style lang="scss" scoped>
  .notes-editor {
    border: 1px solid var(--slate-200);
    border-radius: 0.375rem;
    overflow: hidden;

    &--has-error {
      border-color: var(--red-500);
    }

    .editor-toolbar {
      display: flex;
      gap: 1rem;
      padding: 0.5rem;
      border-bottom: 1px solid var(--slate-200);
      background: var(--slate-50);
      flex-wrap: wrap;

      .toolbar-section {
        display: flex;
        gap: 0.5rem;
        flex-wrap: wrap;
      }
    }

    .toolbar-button {
      padding: 0.25rem 0.5rem;
      border: 1px solid var(--slate-200);
      background: white;
      border-radius: 0.25rem;
      cursor: pointer;
      color: var(--slate-600);
      font-size: 0.875rem;
      display: flex;
      align-items: center;
      gap: 0.25rem;

      &:hover {
        background: var(--slate-100);
      }

      &.field-button {
        &--formula {
          color: var(--green-500);
          background-color: var(--green-100);
          border-color: var(--green-500);
        }

        &--slider,
        &--number {
          color: var(--blue-500);
          background-color: var(--blue-100);
          border-color: var(--blue-500);
        }

        &--text,
        &--long-answer {
          color: var(--purple-500);
          background-color: var(--purple-100);
          border-color: var(--purple-500);
        }
      }
    }

    .editor-content {
      padding: 0.75rem;
      min-height: 100px;
      background: white;
      overflow-x: auto;

      :deep(.ProseMirror) {
        outline: none;

        > * + * {
          margin-top: 0.75em;
        }

        p.is-empty::before {
          content: 'Enter your notes here...';
          float: left;
          color: var(--slate-400);
          pointer-events: none;
          height: 0;
        }

        .field-tag {
          background: var(--brand-50);
          border: 1px solid var(--brand-200);
          padding: 0.125rem 0.375rem;
          border-radius: 0.25rem;
          font-size: 0.875rem;
          white-space: nowrap;
          display: inline-block;
          margin: 0 0.125rem;
          max-width: 220px;
          overflow: hidden;
          text-overflow: ellipsis;

          &--formula {
            color: var(--green-500);
            background-color: var(--green-100);
            border-color: var(--green-500);
          }

          &--slider,
          &--number {
            color: var(--blue-500);
            background-color: var(--blue-100);
            border-color: var(--blue-500);
          }

          &--text,
          &--long-answer {
            color: var(--purple-500);
            background-color: var(--purple-100);
            border-color: var(--purple-500);
          }
        }

        .field-tag-wrapper {
          position: relative;
          display: inline-block;

          &:hover::after {
            content: attr(data-field-label);
            position: absolute;
            bottom: 100%;
            left: 50%;
            transform: translateX(-50%);
            background: var(--slate-800);
            color: white;
            padding: 0.25rem 0.5rem;
            border-radius: 0.25rem;
            font-size: 0.75rem;
            white-space: nowrap;
            z-index: 10;
            opacity: 0;
            animation: fadeIn 0.2s ease-in-out 0.5s forwards;
          }
        }
      }
    }
  }

  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translate(-50%, 0.5rem);
    }
    to {
      opacity: 1;
      transform: translate(-50%, 0);
    }
  }
</style>
